import { useEffect, useRef } from "react";
import {
  invokeCorpusEmbed,
  type CorpusDocumentRow,
} from "@/lib/workspaceCorpus";

const IDLE_MS = 5 * 60 * 1000; // 5 minutes after last edit

/**
 * Watches the currently-edited Corpus document. When its embedding_status is
 * "stale", schedules an auto re-embed 5 minutes after the last observed edit
 * (i.e. the last time `editTick` changes). User can still hit the manual
 * "Re-embed" button at any time.
 */
export function useCorpusEmbedding(
  doc: Pick<CorpusDocumentRow, "id" | "embedding_status"> | null | undefined,
  editTick: number,
): void {
  const timerRef = useRef<number | null>(null);
  const lastDocId = useRef<string | null>(null);

  useEffect(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!doc || doc.embedding_status !== "stale") {
      lastDocId.current = doc?.id ?? null;
      return;
    }
    const docId = doc.id;
    lastDocId.current = docId;
    timerRef.current = window.setTimeout(() => {
      void invokeCorpusEmbed(docId).catch(() => {
        // Silent — UI status pill will reflect any error on next refresh.
      });
    }, IDLE_MS);

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // Only re-arm when the doc identity, server status, or edit tick changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id, doc?.embedding_status, editTick]);
}
